import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Key, Save, FileDown, User, Eye } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { SignatureCanvas } from "@/components/SignatureCanvas";
import { exportKeyHandoverToPDF } from "@/lib/contractPdfExport";
import { saveDocumentToEmployeeFile, getDocumentCategory, getDocumentTypeName } from "@/lib/documentStorage";
import { PdfPreviewDialog } from "./PdfPreviewDialog";

interface Employee {
  id: string;
  name: string;
  position: string;
  employee_number: string | null;
  address_street: string | null;
  address_zip: string | null;
  address_city: string | null;
}

interface CompanySettings {
  name: string | null;
  street: string | null;
  house_number: string | null;
  postal_code: string | null;
  city: string | null;
}

const KEY_TYPES = [
  "Transponder",
  "Schlüssel",
  "Zugangskarte",
  "Chip",
  "Sonstiges",
];

export const KeyHandoverForm = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  const [showSignature, setShowSignature] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<any>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [formData, setFormData] = useState({
    selectedEmployeeId: "",
    personName: "",
    employeeNumber: "",
    personAddressStreet: "",
    personAddressZip: "",
    personAddressCity: "",
    closingTimes: "MO-FR 05.30 Uhr bis 19.00 Uhr",
    alarmPin: "",
    keyType: "Transponder",
    keyNumber: "",
    keyDescription: "",
    notes: "",
    documentDate: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [employeesRes, companyRes] = await Promise.all([
        supabase
          .from("employees")
          .select("id, name, position, employee_number, address_street, address_zip, address_city")
          .is("archived_at", null),
        supabase.from("company_settings").select("name, street, house_number, postal_code, city").single(),
      ]);
      if (employeesRes.data) setEmployees(employeesRes.data as any);
      if (companyRes.data) setCompanySettings(companyRes.data);
    } catch (e) {
      console.error("Error fetching data:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeSelect = (employeeId: string) => {
    const emp = employees.find((e) => e.id === employeeId);
    if (!emp) return;
    setFormData((prev) => ({
      ...prev,
      selectedEmployeeId: employeeId,
      personName: emp.name,
      employeeNumber: emp.employee_number || "",
      personAddressStreet: emp.address_street || "",
      personAddressZip: emp.address_zip || "",
      personAddressCity: emp.address_city || "",
    }));
  };

  const buildCustomText = () =>
    JSON.stringify({
      employeeNumber: formData.employeeNumber,
      closingTimes: formData.closingTimes,
      alarmPin: formData.alarmPin,
      keyType: formData.keyType,
      keyNumber: formData.keyNumber,
      keyDescription: formData.keyDescription,
      notes: formData.notes,
    });

  const buildPdfData = (signatureUrl?: string) => ({
    personName: formData.personName,
    employeeNumber: formData.employeeNumber,
    personAddressStreet: formData.personAddressStreet,
    personAddressZip: formData.personAddressZip,
    personAddressCity: formData.personAddressCity,
    closingTimes: formData.closingTimes,
    alarmPin: formData.alarmPin,
    keyType: formData.keyType,
    keyNumber: formData.keyNumber,
    keyDescription: formData.keyDescription,
    notes: formData.notes,
    documentDate: formData.documentDate,
    companyName: companySettings?.name || "FlashTec-Solution GmbH",
    companyCity: companySettings?.city || "Erftstadt",
    signatureUrl,
  });

  const handleSave = async (signatureUrl?: string) => {
    if (!user) return;
    if (!formData.selectedEmployeeId) {
      toast.error("Bitte wählen Sie einen Mitarbeiter aus");
      return;
    }
    if (!formData.keyNumber) {
      toast.error("Bitte geben Sie eine Schlüssel-/Transpondernummer ein");
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.from("contract_templates").insert({
        template_type: "key_handover",
        employee_id: formData.selectedEmployeeId,
        person_name: formData.personName,
        person_address_street: formData.personAddressStreet,
        person_address_zip: formData.personAddressZip,
        person_address_city: formData.personAddressCity,
        effective_date: formData.documentDate,
        issue_date: formData.documentDate,
        custom_text: buildCustomText(),
        signature_url: signatureUrl,
        signed_at: signatureUrl ? new Date().toISOString() : null,
        signed_by: signatureUrl ? formData.selectedEmployeeId : null,
        status: signatureUrl ? "signed" : "draft",
        created_by: user.id,
      });
      if (error) throw error;

      try {
        const pdfDoc = await exportKeyHandoverToPDF(buildPdfData(signatureUrl), false);
        const category = getDocumentCategory("key_handover");
        const typeName = getDocumentTypeName("key_handover");
        const result = await saveDocumentToEmployeeFile(
          formData.selectedEmployeeId,
          `${typeName} - ${formData.keyType} ${formData.keyNumber}`,
          category,
          pdfDoc,
          user.id
        );
        if (result.success) {
          toast.success("Übergabeprotokoll gespeichert und in Dokumente abgelegt");
        } else {
          toast.error("Gespeichert, aber PDF-Ablage fehlgeschlagen");
        }
      } catch (e) {
        console.error("Error archiving PDF:", e);
        toast.error("Gespeichert, aber PDF-Ablage fehlgeschlagen");
      }

      setShowSignature(false);
      resetForm();
    } catch (e) {
      console.error("Error saving key handover:", e);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!formData.personName || !formData.keyNumber) {
      toast.error("Bitte füllen Sie Mitarbeiter und Schlüsselnummer aus");
      return;
    }
    try {
      const pdfDoc = await exportKeyHandoverToPDF(buildPdfData(), false);
      setPreviewDoc(pdfDoc);
      setPreviewOpen(true);
    } catch (e) {
      console.error("Error generating preview:", e);
      toast.error("Fehler beim Erstellen der Vorschau");
    }
  };

  const handleExportPDF = async () => {
    if (!formData.personName || !formData.keyNumber) {
      toast.error("Bitte füllen Sie Mitarbeiter und Schlüsselnummer aus");
      return;
    }
    try {
      const pdfDoc = await exportKeyHandoverToPDF(buildPdfData(), false);
      if (formData.selectedEmployeeId && user) {
        const category = getDocumentCategory("key_handover");
        const typeName = getDocumentTypeName("key_handover");
        await saveDocumentToEmployeeFile(
          formData.selectedEmployeeId,
          `${typeName} - ${formData.keyType} ${formData.keyNumber}`,
          category,
          pdfDoc,
          user.id
        );
      }
      pdfDoc.save(`Schluesseluebergabe_${formData.personName.replace(/\s+/g, "_")}.pdf`);
      toast.success("PDF wurde erstellt");
    } catch (e) {
      console.error("Error exporting PDF:", e);
      toast.error("Fehler beim Erstellen des PDF");
    }
  };

  const resetForm = () => {
    setFormData({
      selectedEmployeeId: "",
      personName: "",
      employeeNumber: "",
      personAddressStreet: "",
      personAddressZip: "",
      personAddressCity: "",
      closingTimes: "MO-FR 05.30 Uhr bis 19.00 Uhr",
      alarmPin: "",
      keyType: "Transponder",
      keyNumber: "",
      keyDescription: "",
      notes: "",
      documentDate: new Date().toISOString().split("T")[0],
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Key className="h-5 w-5 text-primary" />
          Schlüssel-/Transponder-Übergabe erstellen
        </CardTitle>
        <CardDescription>
          Übergabeprotokoll für Schlüssel, Transponder oder andere Zugangsträger
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Employee Selection */}
        <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-medium">Mitarbeiter auswählen</h4>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Mitarbeiter *</Label>
              <Select value={formData.selectedEmployeeId} onValueChange={handleEmployeeSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Mitarbeiter wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.name} {emp.employee_number ? `(${emp.employee_number})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Dokumentendatum</Label>
              <Input
                type="date"
                value={formData.documentDate}
                onChange={(e) => setFormData((p) => ({ ...p, documentDate: e.target.value }))}
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label>Personalnummer</Label>
              <Input
                value={formData.employeeNumber}
                onChange={(e) => setFormData((p) => ({ ...p, employeeNumber: e.target.value }))}
              />
            </div>
            <div className="md:col-span-2">
              <Label>Name</Label>
              <Input value={formData.personName} readOnly className="bg-muted" />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label>Straße</Label>
              <Input
                value={formData.personAddressStreet}
                onChange={(e) => setFormData((p) => ({ ...p, personAddressStreet: e.target.value }))}
              />
            </div>
            <div>
              <Label>PLZ</Label>
              <Input
                value={formData.personAddressZip}
                onChange={(e) => setFormData((p) => ({ ...p, personAddressZip: e.target.value }))}
              />
            </div>
            <div>
              <Label>Stadt</Label>
              <Input
                value={formData.personAddressCity}
                onChange={(e) => setFormData((p) => ({ ...p, personAddressCity: e.target.value }))}
              />
            </div>
          </div>
        </div>

        {/* Object data */}
        <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-medium">Objektdaten</h4>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Schließzeiten</Label>
              <Input
                value={formData.closingTimes}
                onChange={(e) => setFormData((p) => ({ ...p, closingTimes: e.target.value }))}
                placeholder="z.B. MO-FR 05.30 Uhr bis 19.00 Uhr"
              />
            </div>
            <div>
              <Label>Alarmanlage-PIN</Label>
              <Input
                value={formData.alarmPin}
                onChange={(e) => setFormData((p) => ({ ...p, alarmPin: e.target.value }))}
                placeholder="z.B. 3619"
              />
            </div>
          </div>
        </div>

        {/* Key data */}
        <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-medium">Übergabe-Gegenstand</h4>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Art *</Label>
              <Select
                value={formData.keyType}
                onValueChange={(v) => setFormData((p) => ({ ...p, keyType: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KEY_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nummer / Kennung *</Label>
              <Input
                value={formData.keyNumber}
                onChange={(e) => setFormData((p) => ({ ...p, keyNumber: e.target.value }))}
                placeholder="z.B. M-D6-11D4-31B2"
              />
            </div>
          </div>
          <div>
            <Label>Bezeichnung / Bereich (optional)</Label>
            <Input
              value={formData.keyDescription}
              onChange={(e) => setFormData((p) => ({ ...p, keyDescription: e.target.value }))}
              placeholder="z.B. Haupteingang, Lager, Büro 2"
            />
          </div>
          <div>
            <Label>Zusätzliche Hinweise (optional)</Label>
            <Textarea
              rows={2}
              value={formData.notes}
              onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))}
            />
          </div>
        </div>

        {/* Signature Section */}
        {showSignature && (
          <div className="space-y-4 p-4 border rounded-lg">
            <h4 className="font-medium">Unterschrift des Empfängers</h4>
            <SignatureCanvas
              onSave={async (signatureDataUrl) => {
                const blob = await fetch(signatureDataUrl).then((r) => r.blob());
                const fileName = `key-handover-signature-${Date.now()}.png`;
                const { error: uploadError } = await supabase.storage
                  .from("employee-profiles")
                  .upload(`signatures/${fileName}`, blob);
                if (uploadError) throw uploadError;
                const { data: urlData } = supabase.storage
                  .from("employee-profiles")
                  .getPublicUrl(`signatures/${fileName}`);
                await handleSave(urlData.publicUrl);
                setShowSignature(false);
              }}
              title="Unterschrift Empfänger"
              description="Bitte unterschreiben Sie zur Bestätigung des Empfangs"
              confirmationText="Ich bestätige hiermit den Empfang des oben genannten Schlüssels/Transponders sowie die Kenntnisnahme der Hinweise zur Schlüsselverwahrung und Rückgabe."
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4 border-t">
          <Button variant="outline" onClick={handlePreview} disabled={saving}>
            <Eye className="h-4 w-4 mr-2" />
            Vorschau
          </Button>
          <Button variant="outline" onClick={handleExportPDF} disabled={saving}>
            <FileDown className="h-4 w-4 mr-2" />
            PDF exportieren
          </Button>
          <Button variant="outline" onClick={() => setShowSignature(true)} disabled={saving}>
            <User className="h-4 w-4 mr-2" />
            Mit Unterschrift speichern
          </Button>
          <Button onClick={() => handleSave()} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Als Entwurf speichern
          </Button>
        </div>
      </CardContent>
      <PdfPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        pdfDoc={previewDoc}
        title="Vorschau: Schlüssel-/Transponder-Übergabe"
        fileName={`Schluesseluebergabe_${formData.personName.replace(/\s+/g, "_") || "Vorschau"}.pdf`}
      />
    </Card>
  );
};
